import { MouseEvent, ReactElement } from 'react'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { Box, IconButton, TextField, Tooltip, useTheme } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import { RESULTS2_DETAIL_ROW_HIGHLIGHT_BG } from '../constants'
import { fmt, fmtUtilization, isVibrationError, utilizationCellSx } from '../utils'

export type UtilisationRowProps = {
  label: string
  maxUtilization: number
  maxUtilizationC90: number
  position: number
  checkType?: MemberCheckType
  numberValue?: string
  allowedOverutilizationPercent?: number
  highlighted?: boolean
  /** Show a settings icon after the name when the check belongs to the active settings tab. */
  settingsTabRelevant?: boolean
  disabled?: boolean
  onNumberChange?: (value: string) => void
  onRowClick?: () => void
  onInfoClick?: (e: MouseEvent<HTMLElement>) => void
}

const UtilisationRow = ({
  label,
  maxUtilization,
  maxUtilizationC90,
  position,
  checkType,
  numberValue = '',
  allowedOverutilizationPercent = 0,
  highlighted,
  settingsTabRelevant = false,
  disabled = false,
  onNumberChange,
  onRowClick,
  onInfoClick,
}: UtilisationRowProps): ReactElement => {
  const theme = useTheme()
  const vibrationError = isVibrationError(checkType, maxUtilization)
  const aSx = vibrationError
    ? {
        background: theme.results.utilization.overutilized.light,
        color: theme.palette.text.primary,
        fontWeight: 600,
      }
    : utilizationCellSx(maxUtilization, theme, allowedOverutilizationPercent)
  const c90Sx = utilizationCellSx(maxUtilizationC90, theme, allowedOverutilizationPercent)
  return (
    <Box
      onClick={onRowClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 50px 50px 50px 8px 35px 28px',
        alignItems: 'center',
        gap: 0.75,
        px: 0.75,
        py: 0.375,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: 12,
        background: highlighted ? RESULTS2_DETAIL_ROW_HIGHLIGHT_BG : 'transparent',
        borderRadius: 1,
        cursor: onRowClick ? 'pointer' : undefined,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          color: theme.palette.text.primary,
          fontFamily: 'Roboto',
        }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Box>
        {settingsTabRelevant && (
          <Tooltip
            title="Einstellbar im aktiven Tab unter „Nachweiseinstellungen“"
            arrow
            disableInteractive
          >
            <SettingsOutlinedIcon
              sx={{ flex: '0 0 auto', fontSize: 12, color: theme.palette.grey[500] }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          textAlign: 'center',
          background: aSx?.background ?? 'transparent',
          color: aSx?.color ?? theme.palette.text.primary,
          fontWeight: aSx?.fontWeight,
          borderRadius: 0.5,
          px: 0.375,
        }}
      >
        {vibrationError ? 'ERR' : fmtUtilization(maxUtilization)}
      </Box>
      <Box
        sx={{
          textAlign: 'center',
          background: c90Sx?.background ?? 'transparent',
          color: c90Sx?.color ?? theme.palette.text.secondary,
          fontWeight: c90Sx?.fontWeight,
          borderRadius: 0.5,
          px: 0.375,
        }}
      >
        {fmtUtilization(maxUtilizationC90)}
      </Box>
      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>{fmt(position)}</Box>
      <span />

      <Box
        sx={{
          width: 35,
          py: 0.375,
          alignSelf: 'center',
          justifySelf: 'center',

          alignItems: 'center',
          display: 'inline-flex',
          justifyContent: 'flex-end',
        }}
      >
        <TextField
          size="small"
          variant="standard"
          value={numberValue}
          onChange={e => onNumberChange?.(e.target.value)}
          disabled={disabled}
          inputProps={{
            inputMode: 'decimal',
            'aria-label': `${label} n`,
          }}
          placeholder="0"
          InputProps={{
            sx: {
              fontSize: 12,
              fontFamily: 'Roboto',
              '& input': {
                textAlign: 'center',
                px: 0.375,
                py: 0.375,
              },
            },
            endAdornment: '%',
          }}
          sx={{
            // width: 50,
            minWidth: 0,
          }}
        />
      </Box>
      <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
        <IconButton size="small" onClick={onInfoClick} title="Details">
          <MaterialSymbol icon="info" size={16} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default UtilisationRow
